import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react'

/** @public */
export type Alias = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

const aliases: Alias[] = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl']
const breakpoints = [20, 32, 40, 48, 64, 80]

const matchMedia = (em: number) =>
  typeof window === 'object' &&
  window.matchMedia !== undefined &&
  window.matchMedia(`screen and (max-width: ${em}em)`)

const getBreakpoint = () => {
  for (let i = 0; i < breakpoints.length; i++) {
    if (matchMedia(breakpoints[i])?.matches) return aliases[i]
  }
  return aliases[aliases.length - 1]
}

const useBreakpointEffect = (setBreakpoint: React.Dispatch<React.SetStateAction<Alias>>) => {
  breakpoints.forEach((size, i) => {
    const currentSize = matchMedia(size)
    if (currentSize) {
      /* istanbul ignore next */ // jest isn't capable of properly testing onchange events. See mock in test file
      currentSize.onchange = (e) => {
        const index = e.matches || i + 1 === aliases.length ? i : i + 1
        setBreakpoint(aliases[index])
      }
    }
  })
}

/** @public */
export const useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState<Alias>()
  useEffect(() => useBreakpointEffect(setBreakpoint), [])
  return breakpoint || getBreakpoint()
}

const context = createContext<Alias>(getBreakpoint())

/** @public */
export const BreakpointProvider = ({ children }: { children?: ReactNode }) => {
  const [breakpoint, setBreakpoint] = useState<Alias>()
  useEffect(() => useBreakpointEffect(setBreakpoint), [])
  return <context.Provider value={breakpoint || getBreakpoint()}>{children}</context.Provider>
}

/** @public */
export const useBreakpointContext = () => useContext(context)
